import { GlobalContext } from '@/model/globalcontext.model';
import router from '@/router/index';
import store from '@/store/index';
import { clearClientSessionAndRedirectGuest } from '@/utils/auth-session-clear';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import { isPlatform } from '@ionic/vue';
import axios, { isAxiosError } from 'axios';

export default class AccountService {
  private globalContext?: GlobalContext;

  constructor(globalContext?: GlobalContext) {
    this.init();
    this.globalContext = globalContext;
  }

  public init(): void {
    this.retrieveProfiles();
  }

  private setOfflineMode(): void {
    Preferences.get({ key: 'jwt' }).then((checkJwt) => {
      if (checkJwt.value && checkJwt.value.length > 0 && this.globalContext) {
        this.globalContext.offlineMode = true;
        router.replace('/today');
      }
    });
  }

  /** Clear auth state; при сети — витрина, без сети — вход. */
  private clearSessionAndRedirectToLogin(sessionExpired = true): void {
    void clearClientSessionAndRedirectGuest(sessionExpired);
  }

  /** Try to refresh JWT via API; returns new token or null. Saves token on success. */
  private async tryRefreshToken(currentToken: string | null): Promise<string | null> {
    if (!currentToken?.trim()) return null;
    const baseUrl = process.env.VUE_APP_API_URL;
    try {
      const res = await CapacitorHttp.post({
        url: `${baseUrl}api/refresh-token`,
        headers: { Authorization: `Bearer ${currentToken}` },
        data: {},
      });
      if (res.status !== 200) return null;
      const newJwt = (res.data as { id_token?: string })?.id_token ?? res.headers?.Authorization?.replace(/^Bearer\s+/i, '') ?? null;
      if (!newJwt) return null;
      await Preferences.set({ key: 'jwt', value: newJwt });
      localStorage.setItem('jhi-authenticationToken', newJwt);
      sessionStorage.removeItem('jhi-authenticationToken');
      return newJwt;
    } catch {
      return null;
    }
  }

  public async retrieveProfiles(): Promise<boolean> {
    if (this.globalContext && this.globalContext.offlineMode) {
      return new Promise(resolve => {
        resolve(true);
      });
    }

    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;

      const timeout = setTimeout(() => {
        this.setOfflineMode();
      }, 15000);

      return new Promise(resolve => {
        CapacitorHttp.request({
          method: 'GET',
          url: `${process.env.VUE_APP_API_URL}management/info`,
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 60000,
        }).then(async response => {
          if (response.status === 401) {
            const newToken = await this.tryRefreshToken(token);
            if (newToken) {
              const retry = await CapacitorHttp.request({
                method: 'GET',
                url: `${process.env.VUE_APP_API_URL}management/info`,
                headers: { Authorization: `Bearer ${newToken}` },
                readTimeout: 60000,
              });
              if (retry.status === 1001) this.retrieveProfiles();
              if (retry.data?.activeProfiles) store.commit('setActiveProfiles', retry.data['activeProfiles']);
              resolve(true);
              return;
            }
            this.clearSessionAndRedirectToLogin(true);
            resolve(false);
            return;
          }
          if (response.status === 1001) {
            this.retrieveProfiles();
          }

          if (response.data && response.data.activeProfiles) {
            store.commit('setActiveProfiles', response.data['activeProfiles']);
          }
          resolve(true);
        }).catch(() => {
          if (token && this.globalContext) {
            this.globalContext.offlineMode = true;
            resolve(true);
          } else {
            resolve(false);
          }
        }).finally(() => {
          clearTimeout(timeout);
        })
      });
    } else {
      return new Promise(resolve => {
        axios
          .get('management/info')
          .then(response => {
            if (response.data && response.data.activeProfiles) {
              store.commit('setActiveProfiles', response.data['activeProfiles']);
            }
            resolve(true);
          })
          .catch((err) => {
            console.log(err);
            router.replace('/badconnection');
            resolve(false);
          });
      });
    }
  }

  public async retrieveAccount(): Promise<boolean> {
    if (this.globalContext && this.globalContext.offlineMode) {
      return new Promise(resolve => {
        resolve(true);
      });
    }

    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      if (!token) {
        return false;
      }

      return new Promise(resolve => {
        CapacitorHttp.request({
          method: 'GET',
          url: `${process.env.VUE_APP_API_URL}api/account`,
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 60000,
        }).then(async response => {
          if (response.status === 401) {
            const newToken = await this.tryRefreshToken(token);
            if (newToken) {
              const retry = await CapacitorHttp.request({
                method: 'GET',
                url: `${process.env.VUE_APP_API_URL}api/account`,
                headers: { Authorization: `Bearer ${newToken}` },
                readTimeout: 60000,
              });
              if (retry.status === 200 && retry.data) {
                store.commit('authenticate');
                const account = retry.data;
                if (account?.authorities) {
                  await Preferences.set({ key: 'cached-authorities', value: JSON.stringify(account.authorities) });
                }
                store.commit('authenticated', account);
                resolve(true);
                return;
              }
            }
            this.clearSessionAndRedirectToLogin(true);
            resolve(false);
            return;
          }
          store.commit('authenticate');
          const account = response.data;
          if (account) {
            store.commit('authenticated', account);
            // Сохраняем роли в localStorage для офлайн-режима
            if (account.authorities && Array.isArray(account.authorities)) {
              await Preferences.set({
                key: 'cached-authorities',
                value: JSON.stringify(account.authorities),
              });
            }
            resolve(true);
          } else {
            Preferences.remove({ key: 'jwt' });
            store.commit('logout');
            resolve(false);
          }
        }).catch(() => {
          if (this.globalContext) {
            this.globalContext.offlineMode = true;
            resolve(true);
          } else {
            resolve(false);
          }
        })
      });
    } else {
      return new Promise(resolve => {
        axios
          .get('api/account')
          .then(async response => {
            store.commit('authenticate');
            const account = response.data;
            if (account) {
              store.commit('authenticated', account);
              // Сохраняем роли в localStorage для офлайн-режима
              if (account.authorities && Array.isArray(account.authorities)) {
                localStorage.setItem('cached-authorities', JSON.stringify(account.authorities));
              }
            } else {
              store.commit('logout');
            }
            resolve(true);
          })
          .catch((err: unknown) => {
            if (isAxiosError(err) && err.response?.status === 401) {
              resolve(false);
              return;
            }
            if (this.globalContext) {
              this.globalContext.offlineMode = true;
              resolve(true);
            } else {
              resolve(false);
            }
          });
      });
    }
  }

  public async hasAnyAuthorityAndCheckAuth(authorities: any): Promise<boolean> {
    if (this.globalContext && this.globalContext.offlineMode) {
      // В офлайн-режиме проверяем кешированные роли
      const cachedAuthorities = await this.getCachedAuthorities();
      if (typeof authorities === 'string') {
        authorities = [authorities];
      }
      
      for (const authority of authorities) {
        if (cachedAuthorities.includes(authority)) {
          return Promise.resolve(true);
        }
      }
      return Promise.resolve(false);
    }

    if (typeof authorities === 'string') {
      authorities = [authorities];
    }

    if (!this.authenticated || !this.userAuthorities) {
      let token;
      if (isPlatform('ios') || isPlatform('android')) {
        const tokenQuery = await Preferences.get({ key: 'jwt' });
        token = tokenQuery.value;
      } else {
        token = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
        if (token)
          await Preferences.set({
            key: 'jwt',
            value: token,
          });
      }
      if (!store.getters.account && !store.getters.logon && token) {
        return this.retrieveAccount();
      } else {
        return Promise.resolve(false);
      }
    }

    for (const authority of authorities) {
      if (this.userAuthorities.includes(authority)) {
        return Promise.resolve(true);
      }
    }

    return Promise.resolve(false);
  }

  private async getCachedAuthorities(): Promise<string[]> {
    try {
      if (isPlatform('ios') || isPlatform('android')) {
        const cached = await Preferences.get({ key: 'cached-authorities' });
        if (cached.value) {
          return JSON.parse(cached.value);
        }
      } else {
        const cached = localStorage.getItem('cached-authorities');
        if (cached) {
          return JSON.parse(cached);
        }
      }
    } catch (error) {
      console.error('Error reading cached authorities:', error);
    }
    return [];
  }

  public get authenticated(): boolean {
    return store.getters.authenticated;
  }

  public get userAuthorities(): any {
    return store.getters.account.authorities;
  }

  /** Убедиться, что текущее устройство имеет сессию; при необходимости создаёт и возвращает новый токен. */
  public async ensureSession(): Promise<string | null> {
    if (this.globalContext?.offlineMode) return null;
    const deviceName = await this.getDeviceName();
    if (isPlatform('ios') || isPlatform('android')) {
      const { value: token } = await Preferences.get({ key: 'jwt' });
      if (!token) return null;
      const res = await CapacitorHttp.request({
        method: 'POST',
        url: `${process.env.VUE_APP_API_URL}api/account/ensure-session`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: { deviceName: deviceName || undefined },
        readTimeout: 10000,
      });
      if (res.status !== 200) return null;
      const body = res.data as { id_token?: string };
      const newToken = body?.id_token ?? (res.headers as Record<string, string>)?.['authorization']?.replace(/^Bearer\s+/i, '');
      if (newToken) {
        await Preferences.set({ key: 'jwt', value: newToken });
        localStorage.setItem('jhi-authenticationToken', newToken);
        sessionStorage.removeItem('jhi-authenticationToken');
        return newToken;
      }
      return null;
    }
    const token = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
    if (!token) return null;
    const { data } = await axios.post<{ id_token?: string }>(
      'api/account/ensure-session',
      { deviceName: deviceName || undefined },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const newToken = data?.id_token;
    if (newToken) {
      localStorage.setItem('jhi-authenticationToken', newToken);
      sessionStorage.removeItem('jhi-authenticationToken');
      return newToken;
    }
    return null;
  }

  private async getDeviceName(): Promise<string> {
    const maxLen = 255;
    try {
      if (Capacitor.isNativePlatform()) {
        const info = await Device.getInfo();
        const parts: string[] = [];
        if (info.name?.trim()) parts.push(info.name.trim());
        else if (info.model?.trim()) parts.push(info.model.trim());
        if (info.platform === 'ios') {
          parts.push(info.osVersion ? `iOS ${info.osVersion}` : 'iOS');
        } else if (info.platform === 'android') {
          if (info.manufacturer?.trim() && info.model?.trim()) parts.push(`${info.manufacturer} ${info.model}`);
          else if (info.model?.trim()) parts.push(info.model);
          parts.push(info.osVersion ? `Android ${info.osVersion}` : 'Android');
        }
        const str = parts.length ? parts.join(', ') : 'Мобильное приложение';
        return str.length > maxLen ? str.substring(0, maxLen) : str;
      }
    } catch {
      // fallback
    }
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      const ua = navigator.userAgent;
      return ua.length > maxLen ? ua.substring(0, maxLen) : ua;
    }
    return 'Веб-браузер';
  }

  /** Список авторизаций (устройств) текущего пользователя */
  public async getSessions(): Promise<{ id: number; deviceName: string; createdAt: string; lastUsedAt: string | null }[]> {
    if (this.globalContext?.offlineMode) return [];
    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      if (!token) return [];
      const res = await CapacitorHttp.request({
        method: 'GET',
        url: `${process.env.VUE_APP_API_URL}api/account/sessions`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 10000,
      });
      if (res.status !== 200 || !Array.isArray(res.data)) return [];
      return res.data;
    }
    const { data } = await axios.get<{ id: number; deviceName: string; createdAt: string; lastUsedAt: string | null }[]>('api/account/sessions');
    return Array.isArray(data) ? data : [];
  }

  /** Завершить сессию (выйти с устройства) по id сессии */
  public async revokeSession(sessionId: number): Promise<boolean> {
    if (this.globalContext?.offlineMode) return false;
    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      if (!token) return false;
      const res = await CapacitorHttp.request({
        method: 'DELETE',
        url: `${process.env.VUE_APP_API_URL}api/account/sessions/${sessionId}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: 10000,
      });
      return res.status === 204;
    }
    const res = await axios.delete(`api/account/sessions/${sessionId}`);
    return res.status === 204;
  }

  public async deleteAccount(): Promise<boolean> {
    if (this.globalContext && this.globalContext.offlineMode) {
      return new Promise(resolve => {
        resolve(false);
      });
    }

    if (isPlatform('ios') || isPlatform('android')) {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      if (!token) {
        return false;
      }

      return new Promise(resolve => {
        CapacitorHttp.request({
          method: 'DELETE',
          url: `${process.env.VUE_APP_API_URL}api/account`,
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 60000,
        }).then(async () => {
          await Preferences.remove({ key: 'jwt' });
          await Preferences.remove({ key: 'cached-authorities' });
          store.commit('logout');
          resolve(true);
        }).catch(() => {
          resolve(false);
        })
      });
    } else {
      return new Promise(resolve => {
        axios
          .delete('api/account')
          .then(() => {
            Preferences.remove({ key: 'jwt' });
            localStorage.removeItem('jhi-authenticationToken');
            sessionStorage.removeItem('jhi-authenticationToken');
            localStorage.removeItem('cached-authorities');
            store.commit('logout');
            resolve(true);
          })
          .catch(() => {
            resolve(false);
          });
      });
    }
  }

}
